// School admin: every published bundle the school can use, approved or rejected for its
// teachers and tagged so they can find it. While the school requires approval, Assign
// Practice offers only approved bundles; the server enforces it either way.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createLogger } from '../../utils/logger';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import {
  ApprovalStatus,
  ContentCatalogEntry,
  ContentTags,
  Difficulty,
  fetchContentCatalog,
  setContentApproval,
} from '../../services/classService';
import {
  cardStyle,
  errorMessage,
  errorStyle,
  hintStyle,
  inputStyle,
  labelStyle,
  linkButtonStyle,
  noticeStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  sectionHeadingStyle,
} from './classesStyles';
import { tagSummary } from './contentTags';

const logger = createLogger('ContentApprovalTab');
const SEARCH_DEBOUNCE_MS = 300;

const STATUSES: ApprovalStatus[] = ['pending', 'approved', 'rejected'];
const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard'];
const TEXT_TAGS = ['skill', 'subject', 'grade', 'language'] as const;
const TAG_MAX_LENGTH: Record<(typeof TEXT_TAGS)[number], number> = { skill: 100, subject: 100, grade: 50, language: 50 };

const APPROVAL_COLORS: Record<ApprovalStatus, { bg: string; text: string }> = {
  pending: { bg: '#fef3c7', text: '#92400e' },
  approved: { bg: '#dcfce7', text: '#166534' },
  rejected: { bg: '#fee2e2', text: '#991b1b' },
};

const approvalBadgeStyle = (status: ApprovalStatus): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: '0.75rem',
  fontWeight: 600,
  background: APPROVAL_COLORS[status].bg,
  color: APPROVAL_COLORS[status].text,
});

interface EditorProps {
  entry: ContentCatalogEntry;
  busy: boolean;
  onSave: (status: ApprovalStatus, tags: ContentTags, notes: string | null) => void;
  onCancel: () => void;
}

const ApprovalEditor: React.FC<EditorProps> = ({ entry, busy, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [tags, setTags] = useState<ContentTags>(entry.tags ?? {});
  const [notes, setNotes] = useState(entry.notes ?? '');
  const idFor = (field: string) => `content-${entry.bundle_id}-${field}`;
  const save = (status: ApprovalStatus) => {
    const cleaned: ContentTags = {};
    TEXT_TAGS.forEach(field => {
      const value = (tags[field] ?? '').trim();
      cleaned[field] = value || null;
    });
    cleaned.difficulty = tags.difficulty || null;
    onSave(status, cleaned, notes.trim() || null);
  };

  return (
    <div style={{ marginTop: '0.75rem' }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {TEXT_TAGS.map(field => (
          <div key={field}>
            <label htmlFor={idFor(field)} style={labelStyle}>
              {t(`studentAdmin.content.tags.${field}`)}
            </label>
            <input
              id={idFor(field)}
              style={{ ...inputStyle, width: 150 }}
              maxLength={TAG_MAX_LENGTH[field]}
              value={tags[field] ?? ''}
              onChange={e => setTags(prev => ({ ...prev, [field]: e.target.value }))}
            />
          </div>
        ))}
        <div>
          <label htmlFor={idFor('difficulty')} style={labelStyle}>
            {t('studentAdmin.content.tags.difficulty')}
          </label>
          <select
            id={idFor('difficulty')}
            style={inputStyle}
            value={tags.difficulty ?? ''}
            onChange={e => setTags(prev => ({ ...prev, difficulty: (e.target.value || null) as Difficulty | null }))}
          >
            <option value="">—</option>
            {DIFFICULTIES.map(d => (
              <option key={d} value={d}>
                {t(`studentAdmin.content.difficulties.${d}`)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div style={{ marginTop: '0.75rem' }}>
        <label htmlFor={idFor('notes')} style={labelStyle}>
          {t('studentAdmin.content.notes')}
        </label>
        <textarea
          id={idFor('notes')}
          rows={2}
          style={{ ...inputStyle, width: '100%' }}
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: '0.75rem', flexWrap: 'wrap' }}>
        <button type="button" style={primaryButtonStyle} disabled={busy} onClick={() => save('approved')}>
          {t('studentAdmin.content.approve')}
        </button>
        <button type="button" style={secondaryButtonStyle} disabled={busy} onClick={() => save('rejected')}>
          {t('studentAdmin.content.reject')}
        </button>
        {entry.approval_status !== 'pending' && (
          <button type="button" style={secondaryButtonStyle} disabled={busy} onClick={() => save('pending')}>
            {t('studentAdmin.content.resetToPending')}
          </button>
        )}
        <button type="button" style={linkButtonStyle} disabled={busy} onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
};

interface Props {
  organizationId?: string | null;
  /** Whether the school requires approval; shown so the admin knows what it changes. */
  approvalRequired: boolean;
}

const ContentApprovalTab: React.FC<Props> = ({ organizationId, approvalRequired }) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<ContentCatalogEntry[] | null>(null);
  const [filter, setFilter] = useState<ApprovalStatus | ''>('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    // A slower, older request must not overwrite the list for the current filters.
    let cancelled = false;
    setError(null);
    fetchContentCatalog({ approvalStatus: filter, search: debouncedSearch }, organizationId)
      .then(loaded => {
        if (!cancelled) setEntries(loaded);
      })
      .catch(err => {
        if (cancelled) return;
        logger.error('Failed to load the content catalog', err);
        setError(errorMessage(err, t('studentAdmin.content.couldNotLoad')));
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, filter, debouncedSearch, t]);

  const save = async (entry: ContentCatalogEntry, status: ApprovalStatus, tags: ContentTags, notes: string | null) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await setContentApproval(entry.bundle_id, { approval_status: status, notes, ...tags }, organizationId);
      setEntries(list =>
        (list ?? [])
          .map(e => (e.bundle_id === updated.bundle_id ? updated : e))
          // A row that no longer matches the status filter leaves the list.
          .filter(e => !filter || e.approval_status === filter)
      );
      setEditing(null);
      setNotice(t(`studentAdmin.content.saved.${status}`, { name: entry.name }));
    } catch (err) {
      logger.error('Failed to save the content approval', err);
      setError(errorMessage(err, t('studentAdmin.content.couldNotSave')));
    } finally {
      setBusy(false);
    }
  };

  const difficultyLabel = (d: Difficulty) => t(`studentAdmin.content.difficulties.${d}`);

  return (
    <section style={cardStyle} aria-labelledby="content-approval-heading">
      <h2 id="content-approval-heading" style={sectionHeadingStyle}>
        {t('studentAdmin.content.title')}
      </h2>
      <p style={{ ...hintStyle, marginTop: 0 }}>
        {approvalRequired ? t('studentAdmin.content.hintRequired') : t('studentAdmin.content.hintNotRequired')}
      </p>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '1rem 0' }}>
        <input
          type="search"
          aria-label={t('studentAdmin.content.search')}
          placeholder={t('studentAdmin.content.search')}
          style={{ ...inputStyle, flex: '1 1 220px' }}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          aria-label={t('studentAdmin.content.approvalFilter')}
          style={inputStyle}
          value={filter}
          onChange={e => setFilter(e.target.value as ApprovalStatus | '')}
        >
          <option value="">{t('common.all')}</option>
          {STATUSES.map(s => (
            <option key={s} value={s}>
              {t(`studentAdmin.content.statuses.${s}`)}
            </option>
          ))}
        </select>
      </div>

      {error && <div style={errorStyle} role="alert">{error}</div>}
      {notice && <div style={noticeStyle} role="status">{notice}</div>}

      {!entries ? (
        !error && <LoadingIndicator message={t('studentAdmin.content.loading')} />
      ) : entries.length === 0 ? (
        <p style={hintStyle}>{t('studentAdmin.content.none')}</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {entries.map(entry => {
            const tagLine = tagSummary(entry.tags, difficultyLabel);
            return (
              <li
                key={entry.bundle_id}
                data-testid="content-entry"
                style={{ borderTop: '1px solid #e2e8f0', padding: '0.75rem 0' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <div>
                    <strong style={{ fontSize: '0.9rem' }}>{entry.name}</strong>{' '}
                    <span style={approvalBadgeStyle(entry.approval_status)}>
                      {t(`studentAdmin.content.statuses.${entry.approval_status}`)}
                    </span>
                    <span style={{ ...hintStyle, display: 'block', margin: '2px 0 0' }}>
                      {[
                        entry.platform_wide ? t('studentAdmin.content.platformContent') : t('studentAdmin.content.schoolContent'),
                        t('studentAdmin.content.itemCount', { count: entry.item_count }),
                        tagLine,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    {entry.description && <span style={{ ...hintStyle, display: 'block', margin: 0 }}>{entry.description}</span>}
                    {entry.notes && editing !== entry.bundle_id && (
                      <span style={{ ...hintStyle, display: 'block', margin: 0, fontStyle: 'italic' }}>{entry.notes}</span>
                    )}
                  </div>
                  {editing !== entry.bundle_id && (
                    <button
                      type="button"
                      style={linkButtonStyle}
                      disabled={busy}
                      aria-label={t('studentAdmin.content.reviewFor', { name: entry.name })}
                      onClick={() => setEditing(entry.bundle_id)}
                    >
                      {t('studentAdmin.content.review')}
                    </button>
                  )}
                </div>
                {editing === entry.bundle_id && (
                  <ApprovalEditor
                    entry={entry}
                    busy={busy}
                    onSave={(status, tags, notes) => save(entry, status, tags, notes)}
                    onCancel={() => setEditing(null)}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default ContentApprovalTab;
